import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Project roadmap — dependency graph with bottleneck radar.
//
// Reads from GET /api/projects/{id}/roadmap which returns the node + edge
// set plus a precomputed critical path and bottleneck list. The layout
// here is a deterministic topological-ish placement: tasks are ordered
// left-to-right by "depth" (max distance from a leaf) so dependencies
// generally flow left to right; nodes at the same depth stack vertically.
//
// No third-party graph library — we render the SVG ourselves so the
// bundle stays light. Layout is recomputed from scratch on every data
// change, which is fine for the typical ~20-task project.
import { notify } from "./Toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
// ── Layout ─────────────────────────────────────────────────────────────
const NODE_W = 200;
// Base height covers the title row + status/assignee row. Checklist
// items add CHECKLIST_LINE_H apiece on top, capped by CHECKLIST_MAX_VISIBLE
// with a "+N more" tail when exceeded — the card grows so users can see
// the sub-steps without drilling into the task.
const NODE_H_BASE = 60;
const CHECKLIST_PAD_TOP = 6;
const CHECKLIST_LINE_H = 14;
const CHECKLIST_MAX_VISIBLE = 4;
const COL_GAP = 50;
const ROW_GAP = 20;
const PADDING = 24;
function nodeHeight(n) {
    const items = n.checklist_items ?? [];
    if (items.length === 0)
        return NODE_H_BASE;
    const shown = Math.min(items.length, CHECKLIST_MAX_VISIBLE);
    const overflow = items.length > CHECKLIST_MAX_VISIBLE ? 1 : 0;
    return (NODE_H_BASE + CHECKLIST_PAD_TOP + (shown + overflow) * CHECKLIST_LINE_H + 4);
}
// Compute each node's depth (max distance from any leaf with no upstream).
// We treat the edge "from depends on to" as a flow from `to` (predecessor)
// to `from` (successor). Depth = longest chain length ending at this node.
function layout(nodes, edges) {
    // Adjacency. Edge "from depends on to" → `to` is the predecessor, so
    // upstream[from] collects predecessors and downstream[to] collects
    // successors. Skip edges that reference a node we don't have.
    const present = new Set(nodes.map((n) => n.id));
    const upstream = {};
    const downstream = {};
    const incident = new Set();
    for (const e of edges) {
        if (!present.has(e.from_task_id) || !present.has(e.to_task_id))
            continue;
        (upstream[e.from_task_id] = upstream[e.from_task_id] || []).push(e.to_task_id);
        (downstream[e.to_task_id] = downstream[e.to_task_id] || []).push(e.from_task_id);
        incident.add(e.from_task_id);
        incident.add(e.to_task_id);
    }
    // Tasks in at least one dependency edge go in the graph; the rest are
    // standalone and rendered separately.
    const connected = nodes.filter((n) => incident.has(n.id));
    const standalone = nodes.filter((n) => !incident.has(n.id));
    if (connected.length === 0) {
        return { placed: [], width: 0, height: 0, standalone };
    }
    // Depth = longest predecessor chain (cycle-guarded). Drives the X column.
    const depthMemo = {};
    function depth(id, seen) {
        if (id in depthMemo)
            return depthMemo[id];
        if (seen.has(id))
            return 0;
        seen.add(id);
        let d = 0;
        for (const u of upstream[id] || [])
            d = Math.max(d, depth(u, seen) + 1);
        seen.delete(id);
        depthMemo[id] = d;
        return d;
    }
    for (const n of connected)
        depth(n.id, new Set());
    // Group by depth column; stable-sort each column by (order_index, title)
    // so the base reading order matches the scheduler's "task X of Y".
    const cols = [];
    let maxDepth = 0;
    for (const n of connected) {
        const d = depthMemo[n.id] ?? 0;
        (cols[d] = cols[d] || []).push(n);
        maxDepth = Math.max(maxDepth, d);
    }
    for (const col of cols) {
        if (!col)
            continue;
        col.sort((a, b) => a.order_index !== b.order_index
            ? a.order_index - b.order_index
            : a.title.localeCompare(b.title));
    }
    // Lane (row) assignment — THE fix. A successor inherits the lane of its
    // predecessor(s) so its connecting arrow runs straight across instead of
    // diagonally down a tall column. Multi-predecessor tasks center on the
    // average lane of their predecessors (arrows fan in). Collisions inside a
    // column bump to the nearest free lane. Each chain root opens a new lane.
    const lane = new Map();
    const usedByDepth = new Map();
    function claimLane(d, preferred) {
        let set = usedByDepth.get(d);
        if (!set) {
            set = new Set();
            usedByDepth.set(d, set);
        }
        const p = Math.max(0, preferred);
        if (!set.has(p)) {
            set.add(p);
            return p;
        }
        for (let delta = 1; delta < 500; delta++) {
            if (!set.has(p + delta)) {
                set.add(p + delta);
                return p + delta;
            }
            if (p - delta >= 0 && !set.has(p - delta)) {
                set.add(p - delta);
                return p - delta;
            }
        }
        set.add(p);
        return p;
    }
    let nextRootLane = 0;
    for (let d = 0; d <= maxDepth; d++) {
        const col = cols[d];
        if (!col)
            continue;
        for (const n of col) {
            const preds = upstream[n.id] || [];
            let preferred;
            if (preds.length === 0) {
                preferred = nextRootLane;
            }
            else {
                const pls = preds
                    .map((p) => lane.get(p))
                    .filter((x) => x != null);
                preferred = pls.length
                    ? Math.round(pls.reduce((a, b) => a + b, 0) / pls.length)
                    : nextRootLane;
            }
            const actual = claimLane(d, preferred);
            lane.set(n.id, actual);
            nextRootLane = Math.max(nextRootLane, actual + 1);
        }
    }
    // Fixed lane height = tallest card + gap, so same-lane cards across
    // columns share a Y and the arrow between them is horizontal.
    const laneHeight = Math.max(...connected.map((n) => nodeHeight(n))) + ROW_GAP;
    let maxLane = 0;
    const placed = [];
    for (let d = 0; d <= maxDepth; d++) {
        const col = cols[d];
        if (!col)
            continue;
        for (const n of col) {
            const l = lane.get(n.id) ?? 0;
            maxLane = Math.max(maxLane, l);
            placed.push({
                node: n,
                x: PADDING + d * (NODE_W + COL_GAP),
                y: PADDING + l * laneHeight,
                h: nodeHeight(n),
            });
        }
    }
    const width = PADDING * 2 + (maxDepth + 1) * (NODE_W + COL_GAP) - COL_GAP;
    const height = PADDING * 2 + (maxLane + 1) * laneHeight - ROW_GAP;
    return { placed, width, height, standalone };
}
// Compute connected components in the undirected dependency graph. Two
// tasks are in the same component iff they're reachable from each other
// via any chain of edges, regardless of direction. The "Move left/right"
// reorder operates within a component only — so reordering one row
// doesn't disturb a parallel row that wasn't part of the user's chain.
function connectedComponents(nodes, edges) {
    const adj = {};
    for (const n of nodes)
        adj[n.id] = new Set();
    for (const e of edges) {
        if (!adj[e.from_task_id])
            adj[e.from_task_id] = new Set();
        if (!adj[e.to_task_id])
            adj[e.to_task_id] = new Set();
        adj[e.from_task_id].add(e.to_task_id);
        adj[e.to_task_id].add(e.from_task_id);
    }
    const components = new Map();
    const visited = new Set();
    for (const n of nodes) {
        if (visited.has(n.id))
            continue;
        const stack = [n.id];
        const group = [];
        while (stack.length) {
            const id = stack.pop();
            if (visited.has(id))
                continue;
            visited.add(id);
            group.push(id);
            for (const next of adj[id] ?? []) {
                if (!visited.has(next))
                    stack.push(next);
            }
        }
        // Every node in `group` shares the same component — index it under
        // each member so lookup is O(1).
        for (const id of group)
            components.set(id, group);
    }
    return components;
}
// Dark, glassmorphism-friendly node colors that sit on the navy roadmap
// canvas (matches the dark mockup). Each status gets a deep-tinted fill +
// a saturated stroke; text stays light (#eaf0ff) across the board, with
// per-line opacity handling the muted sublines.
function statusColor(status, isBottleneck) {
    const LIGHT = "#eaf0ff";
    if (isBottleneck)
        return { fill: "#3a1620", stroke: "#ef6461", textColor: LIGHT };
    if (status === "completed")
        return { fill: "#0f3a2f", stroke: "#46d39a", textColor: LIGHT };
    if (status === "in_progress")
        return { fill: "#12305f", stroke: "#4b6cb7", textColor: LIGHT };
    if (status === "blocked")
        return { fill: "#3a2e0c", stroke: "#f0b537", textColor: LIGHT };
    // not started — dark navy surface, subtle light border.
    return { fill: "#0e2552", stroke: "rgba(255,255,255,0.22)", textColor: LIGHT };
}
// ── Component ──────────────────────────────────────────────────────────
export default function RoadmapView({ projectId, canManage, refreshKey, }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Per-card "Depends on…" picker — appears inside the click-menu
    // toolbar when the user wants to wire this task to predecessors.
    // Holds an array of task ids the user has toggled on; clicking
    // "+ Link N" POSTs each as a new dependency edge. Switched from a
    // single-select dropdown to multi-select chips so a task can wait
    // on two or more predecessors in one go.
    const [dependsOnPickedIds, setDependsOnPickedIds] = useState([]);
    const [actionBusy, setActionBusy] = useState(false);
    function togglePickedDep(id) {
        setDependsOnPickedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }
    async function load() {
        setLoading(true);
        setError(null);
        try {
            const d = await api.get(`/projects/${projectId}/roadmap`);
            setData(d);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load roadmap");
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // refreshKey changes when the parent adds/removes a task or
        // checklist item, so the roadmap re-fetches without a page reload.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, refreshKey]);
    const { placed, width, height, standalone } = useMemo(() => {
        if (!data)
            return {
                placed: [],
                width: 800,
                height: 200,
                standalone: [],
            };
        return layout(data.nodes, data.edges);
    }, [data]);
    const placedById = useMemo(() => {
        const m = new Map();
        for (const p of placed)
            m.set(p.node.id, p);
        return m;
    }, [placed]);
    // All nodes by id (graph + standalone) so the action toolbar can open
    // for a standalone card too — clicking one to add its first dependency
    // is how a loose task joins a chain.
    const nodeById = useMemo(() => {
        const m = new Map();
        if (data)
            for (const n of data.nodes)
                m.set(n.id, n);
        return m;
    }, [data]);
    async function addDependenciesOnto(taskId, predecessorIds) {
        // Filter junk values (self-edges, duplicates) defensively even
        // though the picker already excludes them — the user might race
        // a state update via keyboard.
        const ids = Array.from(new Set(predecessorIds.filter((p) => p && p !== taskId)));
        if (!taskId || ids.length === 0)
            return;
        setActionBusy(true);
        // Track which edges actually landed so we can clear them from
        // the picker selection. A partial failure (e.g. one creates a
        // cycle) leaves the offending pick in place so the user can see
        // what went wrong.
        const succeeded = new Set();
        let firstError = null;
        for (const pid of ids) {
            try {
                await api.post(`/projects/${projectId}/dependencies`, {
                    from_task_id: taskId,
                    to_task_id: pid,
                });
                succeeded.add(pid);
            }
            catch (e) {
                if (!firstError)
                    firstError = e;
            }
        }
        setDependsOnPickedIds((prev) => prev.filter((x) => !succeeded.has(x)));
        await load();
        setActionBusy(false);
        if (firstError) {
            if (firstError instanceof ApiError) {
                notify.error(typeof firstError.body === "object" &&
                    firstError.body &&
                    "detail" in firstError.body
                    ? String(firstError.body.detail)
                    : firstError.message);
            }
            else {
                notify.error(firstError instanceof Error
                    ? firstError.message
                    : "Couldn't add one or more dependencies");
            }
        }
    }
    async function detachTask(taskId) {
        // Pulls the task out of every chain it currently sits in — drops all
        // edges incident to it server-side. The next render puts it back at
        // depth 0 in its own row, ready to become the head of a new path.
        setActionBusy(true);
        try {
            await api.post(`/projects/${projectId}/tasks/${taskId}/detach`, {});
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't start a new path for this task");
        }
        finally {
            setActionBusy(false);
        }
    }
    async function removeDependency(edgeId) {
        if (!confirm("Remove this dependency?"))
            return;
        try {
            await api.del(`/projects/${projectId}/dependencies/${edgeId}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't remove dependency");
        }
    }
    // ── Click-menu reordering ──────────────────────────────────────────
    // Drag was laggy on a hot SVG canvas; replaced with a simpler click
    // interaction: tap a node → small menu with Move left / Move right
    // that swaps the node with its neighbor in the current order. Each
    // tap PUTs the rebuilt order to the backend.
    const svgRef = useRef(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [reorderBusy, setReorderBusy] = useState(false);
    useEffect(() => {
        // Click-anywhere-else closes the open menu.
        if (!openMenuId)
            return;
        function onDoc() {
            setOpenMenuId(null);
        }
        document.addEventListener("click", onDoc);
        return () => document.removeEventListener("click", onDoc);
    }, [openMenuId]);
    // Current visual ordering — placed[] is already sorted by depth
    // ascending then by intra-column position, so reading IDs off it
    // gives left-to-right.
    const currentOrder = useMemo(() => placed.map((p) => p.node.id), [placed]);
    // Connected-component lookup keyed by node id. Two tasks share a
    // component iff they're reachable via any chain of edges (ignoring
    // direction). Move-left / move-right reorder operations are scoped
    // to the clicked task's component so parallel rows stay independent.
    const components = useMemo(() => {
        if (!data)
            return new Map();
        return connectedComponents(data.nodes, data.edges);
    }, [data]);
    // The ordered slice of currentOrder restricted to the given task's
    // component — used both to drive Move left/right and to decide
    // whether those buttons should be enabled.
    function componentChainOf(nodeId) {
        const member = components.get(nodeId);
        if (!member)
            return [nodeId];
        const memberSet = new Set(member);
        return currentOrder.filter((id) => memberSet.has(id));
    }
    async function moveNode(nodeId, direction) {
        if (!data)
            return;
        const chain = componentChainOf(nodeId);
        const idx = chain.indexOf(nodeId);
        if (idx < 0)
            return;
        const targetIdx = idx + direction;
        if (targetIdx < 0 || targetIdx >= chain.length)
            return;
        const next = [...chain];
        [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
        setReorderBusy(true);
        setOpenMenuId(null);
        try {
            // Pass only this chain's IDs — the backend wipes edges only between
            // these tasks and rebuilds them, leaving parallel rows untouched.
            await api.put(`/projects/${projectId}/dependencies/order`, {
                ordered_task_ids: next,
            });
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't save the new task order");
        }
        finally {
            setReorderBusy(false);
        }
    }
    if (loading)
        return _jsx("div", { className: "muted", children: "Loading roadmap\u2026" });
    if (error)
        return _jsx("div", { className: "error", children: error });
    if (!data)
        return null;
    return (_jsxs("div", { className: "roadmap", children: [data.bottlenecks.length > 0 && (_jsxs("div", { className: "roadmap-bottlenecks", children: [_jsxs("div", { className: "roadmap-bottlenecks-head", children: ["\u26A0 ", data.bottlenecks.length, " bottleneck", data.bottlenecks.length === 1 ? "" : "s", " slowing this project"] }), _jsx("ul", { children: data.bottlenecks.map((b) => (_jsxs("li", { children: [_jsx("strong", { children: b.task_title }), " \u2014 ", b.reason, b.holders.length > 0 && (_jsxs("span", { className: "muted small", children: [" ", "\u00B7 holder", b.holders.length === 1 ? "" : "s", ":", " ", b.holders.join(", ")] }))] }, b.task_id))) })] })), data.nodes.length === 0 && (_jsx("div", { className: "muted", children: "No tasks on this project yet. Add tasks first, then connect them." })), data.nodes.length > 0 && (_jsxs("div", { className: "roadmap-canvas-wrap", style: { position: "relative" }, children: [canManage && placed.length > 0 && (_jsx("div", { className: "muted small roadmap-drag-hint", children: "Click a task to reorder it within its row, start a new row below, or link it to another task it depends on. Tasks with no dependency on each other sit in the first column; tasks with no dependency at all drop into the grid below." })), openMenuId && canManage && (() => {
                        const selectedNode = nodeById.get(openMenuId);
                        if (!selectedNode)
                            return null;
                        const chain = componentChainOf(openMenuId);
                        const idx = chain.indexOf(openMenuId);
                        const canLeft = idx > 0;
                        const canRight = idx >= 0 && idx < chain.length - 1;
                        // The task currently has at least one edge if its
                        // component is bigger than just itself — that's the
                        // signal "Start new path below" is meaningful.
                        const isInChain = chain.length > 1;
                        // Build the "Depends on…" candidate list: every OTHER
                        // task in this project, except those already directly
                        // depended on by this task. We can't cleanly forbid
                        // cycles client-side (the backend already rejects them
                        // with 400), so the picker just shows everything and we
                        // let the server enforce.
                        const alreadyDependedOn = new Set(data.edges
                            .filter((e) => e.from_task_id === openMenuId)
                            .map((e) => e.to_task_id));
                        const candidates = data.nodes.filter((n) => n.id !== openMenuId && !alreadyDependedOn.has(n.id));
                        return (_jsxs("div", { className: "roadmap-reorder-toolbar", onClick: (e) => e.stopPropagation(), children: [_jsxs("span", { className: "roadmap-reorder-label", children: ["Selected:", " ", _jsx("strong", { children: selectedNode.title }), idx >= 0 && isInChain && (_jsxs("span", { className: "muted small", children: [" ", "\u00B7 ", idx + 1, " of ", chain.length, " in this row"] }))] }), _jsxs("div", { className: "roadmap-reorder-actions", children: [_jsx("button", { type: "button", disabled: !canLeft || reorderBusy || actionBusy, onClick: () => moveNode(openMenuId, -1), children: "\u2190 Move left" }), _jsx("button", { type: "button", disabled: !canRight || reorderBusy || actionBusy, onClick: () => moveNode(openMenuId, +1), children: "Move right \u2192" }), _jsx("button", { type: "button", disabled: !isInChain || actionBusy, onClick: () => detachTask(openMenuId), title: "Drop this task's dependencies and start a fresh row", children: "\u2193 Start new path" }), _jsx("div", { className: "roadmap-depson-picker", role: "group", "aria-label": "Pick prerequisite tasks", children: candidates.length === 0 ? (_jsx("span", { className: "muted small", children: "No other tasks to link to" })) : (candidates.map((c) => {
                                                const isOn = dependsOnPickedIds.includes(c.id);
                                                return (_jsxs("button", { type: "button", className: `roadmap-depson-chip ${isOn ? "active" : ""}`, disabled: actionBusy, onClick: () => togglePickedDep(c.id), title: isOn
                                                        ? "Click to remove this prerequisite"
                                                        : "Click to mark this task as a prerequisite", children: [isOn ? "✓ " : "+ ", c.title] }, c.id));
                                            })) }), _jsx("button", { type: "button", disabled: dependsOnPickedIds.length === 0 || actionBusy, onClick: () => addDependenciesOnto(openMenuId, dependsOnPickedIds), children: dependsOnPickedIds.length > 1
                                                ? `+ Link ${dependsOnPickedIds.length}`
                                                : "+ Link" }), _jsx("button", { type: "button", className: "roadmap-reorder-close", onClick: () => {
                                                setDependsOnPickedIds([]);
                                                setOpenMenuId(null);
                                            }, "aria-label": "Close action toolbar", children: "\u2715" })] })] }));
                    })(), placed.length > 0 && (_jsxs("svg", { ref: svgRef, viewBox: `0 0 ${width} ${height}`, width: width, height: height, style: { display: "block", width, height }, xmlns: "http://www.w3.org/2000/svg", children: [_jsxs("defs", { children: [_jsx("marker", { id: "rm-arrow", viewBox: "0 0 8 8", refX: "7", refY: "4", markerWidth: "6", markerHeight: "6", orient: "auto", children: _jsx("path", { d: "M0 0 L8 4 L0 8 z", fill: "rgba(255,255,255,0.55)" }) }), _jsx("marker", { id: "rm-arrow-crit", viewBox: "0 0 8 8", refX: "7", refY: "4", markerWidth: "7", markerHeight: "7", orient: "auto", children: _jsx("path", { d: "M0 0 L8 4 L0 8 z", fill: "#E24B4A" }) })] }), data.edges.map((e) => {
                                const from = placedById.get(e.from_task_id);
                                const to = placedById.get(e.to_task_id);
                                if (!from || !to)
                                    return null;
                                // The edge "from depends on to" means the arrow points from
                                // `to` (predecessor) into `from` (successor). Draw left-to-
                                // right whenever depths place them that way. Use the per-
                                // card height (`h`) to anchor at the mid-point of each
                                // card — cards with checklist previews are taller than
                                // bare ones, so a fixed NODE_H would float arrows.
                                const startX = to.x + NODE_W;
                                const startY = to.y + to.h / 2;
                                const endX = from.x;
                                const endY = from.y + from.h / 2;
                                const onCrit = e.on_critical_path;
                                return (_jsxs("g", { className: "roadmap-edge", children: [_jsx("line", { x1: startX, y1: startY, x2: endX, y2: endY, stroke: onCrit ? "#E24B4A" : "rgba(255,255,255,0.35)", strokeWidth: onCrit ? 2.5 : 1.5, markerEnd: onCrit ? "url(#rm-arrow-crit)" : "url(#rm-arrow)" }), canManage && (_jsx("line", { x1: startX, y1: startY, x2: endX, y2: endY, stroke: "transparent", strokeWidth: 14, style: { cursor: "pointer" }, onClick: () => removeDependency(e.id), children: _jsx("title", { children: "Click to remove this dependency" }) }))] }, e.id));
                            }), placed.map(({ node, x, y, h }) => {
                                const c = statusColor(node.status, node.is_bottleneck);
                                const isOpen = openMenuId === node.id;
                                const items = node.checklist_items ?? [];
                                const visibleItems = items.slice(0, CHECKLIST_MAX_VISIBLE);
                                const overflowCount = Math.max(0, items.length - CHECKLIST_MAX_VISIBLE);
                                // Where the checklist preview starts vertically inside the
                                // card. After the title (y+22) and status row (y+40), pad
                                // a little and then list each item on its own row.
                                const checklistTop = y + 48 + CHECKLIST_PAD_TOP;
                                return (_jsxs("g", { onClick: (e) => {
                                        if (!canManage)
                                            return;
                                        // Stop the document-level close handler from
                                        // immediately firing right after we set the state.
                                        e.stopPropagation();
                                        setOpenMenuId(isOpen ? null : node.id);
                                        // Switching cards clears any prior multi-select
                                        // chip state — the picks are scoped to whichever
                                        // task the toolbar is open on.
                                        setDependsOnPickedIds([]);
                                    }, style: {
                                        cursor: canManage ? "pointer" : "default",
                                        opacity: reorderBusy && isOpen ? 0.6 : 1,
                                    }, children: [_jsx("rect", { x: x, y: y, width: NODE_W, height: h, rx: 10, fill: c.fill, stroke: isOpen ? "#185FA5" : c.stroke, strokeWidth: isOpen ? 2.5 : node.is_bottleneck ? 2 : 1 }), node.is_bottleneck && (_jsx("circle", { cx: x + 9, cy: y + 9, r: 4, fill: "#E24B4A" })), _jsx("text", { x: x + NODE_W / 2, y: y + 22, textAnchor: "middle", fontSize: "12", fontWeight: "500", fill: c.textColor, style: { pointerEvents: "none", userSelect: "none" }, children: node.title.length > 26
                                                ? node.title.slice(0, 25) + "…"
                                                : node.title }), _jsxs("text", { x: x + NODE_W / 2, y: y + 40, textAnchor: "middle", fontSize: "10", fill: c.textColor, opacity: "0.85", style: { pointerEvents: "none", userSelect: "none" }, children: [node.status.replace("_", " "), " \u00B7", " ", node.assignee_names.length === 0
                                                    ? "Unassigned"
                                                    : node.assignee_names[0] +
                                                        (node.assignee_names.length > 1
                                                            ? ` +${node.assignee_names.length - 1}`
                                                            : "")] }), visibleItems.map((ci, i) => {
                                            const itemY = checklistTop + i * CHECKLIST_LINE_H + 9;
                                            const marker = ci.is_completed ? "✓" : "○";
                                            // Compose "Title (Assignee)" then truncate. We cap
                                            // the assignee at 12 chars to keep the row from
                                            // blowing out the card width on long names.
                                            const assigneeFragment = ci.assignee_name
                                                ? ` (${ci.assignee_name.length > 12
                                                    ? ci.assignee_name.slice(0, 11) + "…"
                                                    : ci.assignee_name})`
                                                : "";
                                            const fullLabel = `${ci.title}${assigneeFragment}`;
                                            const label = fullLabel.length > 26
                                                ? fullLabel.slice(0, 25) + "…"
                                                : fullLabel;
                                            return (_jsxs("text", { x: x + 10, y: itemY, fontSize: "10", fill: c.textColor, opacity: ci.is_completed ? 0.55 : 0.88, style: { pointerEvents: "none", userSelect: "none" }, textDecoration: ci.is_completed ? "line-through" : undefined, children: [_jsxs("tspan", { fontWeight: "500", children: [marker, " "] }), label] }, ci.id));
                                        }), overflowCount > 0 && (_jsxs("text", { x: x + 10, y: checklistTop +
                                                visibleItems.length * CHECKLIST_LINE_H +
                                                9, fontSize: "10", fill: c.textColor, opacity: "0.7", fontStyle: "italic", style: { pointerEvents: "none", userSelect: "none" }, children: ["+ ", overflowCount, " more"] }))] }, node.id));
                            })] })), standalone.length > 0 && (_jsxs("div", { className: "roadmap-standalone", children: [_jsxs("div", { className: "roadmap-standalone-head", children: ["No dependencies \u2014 not in any chain \u00B7 ", standalone.length] }), _jsx("div", { className: "roadmap-standalone-grid", children: standalone.map((n) => {
                                    const isOpen = openMenuId === n.id;
                                    const c = statusColor(n.status, n.is_bottleneck);
                                    return (_jsxs("button", { type: "button", className: `roadmap-standalone-card ${isOpen ? "selected" : ""}`, style: { cursor: canManage ? "pointer" : "default" }, onClick: (e) => {
                                            if (!canManage)
                                                return;
                                            e.stopPropagation();
                                            setOpenMenuId(isOpen ? null : n.id);
                                            setDependsOnPickedIds([]);
                                        }, children: [_jsx("span", { className: "roadmap-standalone-dot", style: { background: c.stroke }, "aria-hidden": "true" }), _jsxs("span", { className: "roadmap-standalone-body", children: [_jsx("span", { className: "roadmap-standalone-title", children: n.title }), _jsxs("span", { className: "roadmap-standalone-sub", children: [n.status.replace("_", " "), " \u00B7", " ", n.assignee_names.length === 0
                                                                ? "Unassigned"
                                                                : n.assignee_names[0] +
                                                                    (n.assignee_names.length > 1
                                                                        ? ` +${n.assignee_names.length - 1}`
                                                                        : "")] })] })] }, n.id));
                                }) }), canManage && (_jsx("div", { className: "muted small roadmap-standalone-hint", children: "Click a task to link it to a prerequisite \u2014 it joins a chain above." }))] })), _jsxs("div", { className: "roadmap-legend", children: [_jsxs("span", { children: [_jsx("span", { className: "rm-swatch rm-swatch-todo" }), "Not started"] }), _jsxs("span", { children: [_jsx("span", { className: "rm-swatch rm-swatch-prog" }), "In progress"] }), _jsxs("span", { children: [_jsx("span", { className: "rm-swatch rm-swatch-done" }), "Done"] }), _jsxs("span", { children: [_jsx("span", { className: "rm-swatch rm-swatch-block" }), "Bottleneck"] }), _jsxs("span", { children: [_jsx("span", { className: "rm-swatch rm-swatch-crit" }), "Critical path"] })] })] }))] }));
}
